import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { GeographicDistributionData } from '../../services/api/artist-analytics.service';
import { useArtist } from '../../contexts/ArtistContext';
import {
  getCategoryAxisLayout,
  maxGridBottomPct,
  splitWordsOnSpace,
} from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface GeographicDistributionBarChartProps {
  artistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
}

interface CityData {
  name: string;
  value: number;
  country: string;
  spotifyListeners: number;
  youtubeViews: number;
}

const GeographicDistributionBarChart: React.FC<GeographicDistributionBarChartProps> = ({
  artistId,
  title = 'Geographic Distribution',
  className = '',
  refreshKey = 0
}) => {
  const { currentArtistId, availableArtists } = useArtist();
  const [data, setData] = useState<CityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get clickhouseArtistId from selected artist
        const selectedArtist = availableArtists.find(a => a.artistId === currentArtistId);
        const clickhouseArtistId = selectedArtist?.clickhouseArtistId;
        
        if (!clickhouseArtistId) {
          setError('ClickHouse artist ID not available');
          setLoading(false);
          return;
        }
        
        const response = await artistAnalyticsService.getGeographicDistribution(clickhouseArtistId);
        
        // Transform country data to city data
        // Use total engagement (Spotify + YouTube) instead of just Spotify listeners
        const cityData: CityData[] = response.data
          .filter(item => item.value > 0) // Filter out items with no engagement
          .map(item => {
            let cityName: string;
            
            // If we have a city name, parse it; otherwise use country name
            if (item.top_city && item.top_city.trim() !== '') {
              cityName = item.top_city.includes(', ') 
                ? item.top_city.split(', ')[0] 
                : item.top_city;
            } else {
              // Fallback to country name if no city data
              cityName = item.name || 'Unknown';
            }
            
            return {
              name: cityName,
              value: item.value, // Total engagement (Spotify + YouTube)
              country: item.name || 'Unknown',
              spotifyListeners: item.spotify_listeners || 0,
              youtubeViews: item.youtube_views || 0
            };
          })
          .sort((a, b) => b.value - a.value)
          .slice(0, 20); // Top 20 cities
        
        setData(cityData);
      } catch (err) {
        setError('Failed to load geographic distribution data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentArtistId, availableArtists, refreshKey]);

  const formatLargeNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const getChartOption = (): EChartsOption => {
    if (!data || data.length === 0) return {};

    const cities = data.map(item => item.name);
    const spotifyValues = data.map(item => item.spotifyListeners);
    const xLayout = getCategoryAxisLayout(cities.length);

    return {
      backgroundColor: '#ffffff',
      color: ['#1DB954'], // Spotify green
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        confine: true,
        position: function (point: any, params: any, dom: any, rect: any, size: any) {
          // Use relative positioning if any required parameter is null
          if (!point || !size || !size.contentSize || !size.viewSize) {
            return [point?.[0] ? point[0] + 20 : 20, point?.[1] || 0];
          }
          
          // For horizontal bar charts, calculate relative position
          const tooltipHeight = size.contentSize[1] || 0;
          const tooltipWidth = size.contentSize[0] || 0;
          const pointX = point[0];
          const pointY = point[1];
          
          const chartHeight = size.viewSize[1] || 0;
          const chartWidth = size.viewSize[0] || 0;
          
          // Position tooltip to the right of the bar
          let tooltipX = pointX + 20;
          let tooltipY = pointY;
          
          // If tooltip would be cut off at bottom, position it above the bar
          if (tooltipHeight > 0 && pointY + tooltipHeight > chartHeight - 30) {
            tooltipY = pointY - tooltipHeight - 15;
          }
          
          // Ensure tooltip doesn't go off the right edge
          if (tooltipWidth > 0 && tooltipX + tooltipWidth > chartWidth - 20) {
            tooltipX = pointX - tooltipWidth - 20;
          }
          
          return [tooltipX, tooltipY];
        },
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        formatter: function(params: any) {
          const param = params[0];
          const dataIndex = param.dataIndex;
          const cityItem = data[dataIndex];
          
          let tooltipHtml = `<div style="font-weight: 600; margin-bottom: 8px; color: #111827;">${cityItem.name}</div>`;
          
          // Show Spotify value
          if (cityItem.spotifyListeners > 0) {
            tooltipHtml += `
              <div style="color: #1DB954; margin-bottom: 4px;">
                ● Spotify: ${formatLargeNumber(cityItem.spotifyListeners)}
              </div>
            `;
          }
          
          tooltipHtml += `
            <div style="color: #9ca3af; font-size: 11px;">
              Country: ${cityItem.country}
            </div>
          `;
          
          return tooltipHtml;
        }
      },
      legend: {
        data: ['Spotify'],
        textStyle: {
          color: '#6b7280',
          fontSize: 11
        },
        top: 5,
        right: 10,
        itemWidth: 14,
        itemHeight: 10,
        itemGap: 10
      },
      grid: {
        left: '8%',
        right: '5%',
        top: '12%',
        bottom: maxGridBottomPct('16%', xLayout.gridBottom),
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: cities,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#d1d5db',
            width: 1
          }
        },
        axisTick: { show: false },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          lineHeight: 13,
          formatter: (value: string) => {
            const v =
              value.length > 18 ? `${value.slice(0, 17)}…` : value;
            return splitWordsOnSpace(v, 11);
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: true,
          lineStyle: {
            color: '#d1d5db',
            width: 1
          }
        },
        axisTick: { show: false },
        axisLabel: {
          color: '#6b7280',
          fontSize: 10,
          formatter: function(value: number) {
            return formatLargeNumber(value);
          }
        },
        splitLine: {
          lineStyle: {
            color: '#d1d5db',
            width: 1
          }
        }
      },
      series: [
        {
          name: 'Spotify',
          type: 'bar',
          color: '#1DB954',
          data: spotifyValues.map((value) => ({
            value: value,
            itemStyle: {
              color: '#1DB954',
              borderRadius: [4, 4, 0, 0]
            }
          })),
          barWidth: 30,
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(29, 185, 84, 0.3)'
            }
          },
          animationDelay: function (idx: number) {
            return idx * 50;
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent px-2 pt-2 pb-1">
      <BaseChart
        option={getChartOption()}
        loading={loading}
        className="w-full h-full"
      />
    </div>
  );
};

export default GeographicDistributionBarChart;
